import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Observable } from "rxjs";
import { environment } from "../../../../environments/environment";
import { Permission, UserPermission } from 'src/app/interfaces/masterpass/policy';
import { ErrorHandlerService } from '../../utilities/error-handler.service';
import { Store } from '@ngrx/store';
import { AuthActions } from 'src/app/store/auth/auth.actions';
import { AuthSnapshotService } from 'src/app/store/auth/auth-snapshot.service';

@Injectable({
  providedIn: 'root'
})
export class PermissionService {

  permissionEndPoint = "/permission/";

  constructor(
    private http: HttpClient,
    private errorHandlerService: ErrorHandlerService,
    private store: Store,
    private snap: AuthSnapshotService
  ) { }

  async getPermission(id: string): Promise<Observable<any>> {
    return this.http.get<any>(environment.apiUrl + this.permissionEndPoint + id);
  }

  async getAllPermission(): Promise<Observable<any>> {
    return this.http.get<any>(environment.apiUrl + this.permissionEndPoint);
  }

  getAllPermissions(): Observable<any> {
    return this.http.get<any>(environment.apiUrl + this.permissionEndPoint);
  }

  async createPermission(data: Permission): Promise<Observable<any>> {
    return this.http.post<any>(environment.apiUrl + this.permissionEndPoint, data);
  }

  async updatePermission(data: Permission): Promise<Observable<any>> {
    return this.http.patch<any>(environment.apiUrl + this.permissionEndPoint, data);
  }

  async deletePermission(id: string): Promise<Observable<any>> {
    return this.http.delete<any>(environment.apiUrl + this.permissionEndPoint + id);
  }

  // User Organisation Permissions
  getUserOrganisationPermission(userId: string): Observable<any> {
    return this.http.get<any>(environment.apiUrl + this.permissionEndPoint + 'user-organisation/' + userId);
  }

  updateUserOrgPermissions(userId: string, data: any): Observable<any> {
    return this.http.patch<any>(environment.apiUrl + this.permissionEndPoint + "user-organisation/" + userId, data);
  }

  // Organisation Permissions
  getOrgPermissions(id: any): Observable<any> {
    return this.http.get<any>(environment.apiUrl + this.permissionEndPoint + "org_permission/" + id);
  }

  updateOrgPermissions(id: any, data: any): Observable<any> {
    return this.http.post<any>(environment.apiUrl + this.permissionEndPoint + "org_permission/" + id, data);
  }

  // Organisation Invite Permissions
  getOrgInvitationPermission(id: any): Observable<any> {
    return this.http.get<any>(environment.apiUrl + this.permissionEndPoint + "organisation-invitation/" + id);
  }

  updateOrgInvitationPermission(id: any, data: any): Observable<any> {
    return this.http.patch<any>(environment.apiUrl + this.permissionEndPoint + "organisation-invitation/" + id, data);
  }

  // Department Permissions
  getDepartmentPermissions(id: any): Observable<any> {
    return this.http.get<any>(environment.apiUrl + this.permissionEndPoint + "department/" + id);
  }

  updateDepartmentPermissions(id: any, data: any): Observable<any> {
    return this.http.patch<any>(environment.apiUrl + this.permissionEndPoint + "department/" + id, data);
  }

  // Role Permissions
  getRolePermissions(id: any): Observable<any> {
    return this.http.get<any>(environment.apiUrl + this.permissionEndPoint + "role/" + id);
  }

  updateRolePermissions(id: any, data: any): Observable<any> {
    return this.http.patch<any>(environment.apiUrl + this.permissionEndPoint + "role/" + id, data);
  }

  /** Drops the cached permissions, e.g. before recomputing them for a new organisation. */
  public clearUserPermission() {
    this.store.dispatch(AuthActions.permissionsCleared());
  }

  /**
   * Stores the resolved permissions.
   *
   * Callers pass a bare `[]` to mean "loading"; the reducer keeps that array shape
   * because checkPermission has a branch that depends on seeing it.
   */
  public saveUserPermission(permission: any) {
    this.store.dispatch(AuthActions.permissionsSet({ permissions: permission }));
  }

  public getUserPermission(): any {
    return this.snap.permissions ?? {};
  }
}
